import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, Calendar, Phone, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ScheduleBlock {
  id: string;
  timeRange: string;
  type: string;
  callCount: number;
}

interface OpenSlot {
  time: string;
  duration: string;
}

const SchedulingPage: React.FC = () => {
  const [scheduleBlocks] = React.useState<ScheduleBlock[]>([
    {
      id: "1",
      timeRange: "09:00 AM - 11:00 AM",
      type: "Appointment Reminders",
      callCount: 15,
    },
    {
      id: "2",
      timeRange: "11:00 AM - 01:00 PM",
      type: "Insurance Verification",
      callCount: 8,
    },
  ]);

  const [openSlots] = React.useState<OpenSlot[]>([
    { time: "2:00 PM", duration: "60min" },
    { time: "3:30 PM", duration: "60min" },
  ]);

  return (
    <div className="m-4 md:m-8">
      <Card className="w-full">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
          <CardTitle>Scheduling</CardTitle>
          <Dialog>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add Schedule
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Schedule Block</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Time Range</label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Select time range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">
                        9:00 AM - 11:00 AM
                      </SelectItem>
                      <SelectItem value="afternoon">
                        1:00 PM - 3:00 PM
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Call Type</label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Select call type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reminders">
                        Appointment Reminders
                      </SelectItem>
                      <SelectItem value="insurance">
                        Insurance Verification
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button className="w-full">Create Schedule</Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Today's Call Schedule */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              Today's Call Schedule
            </h2>
            <div className="space-y-4">
              {scheduleBlocks.map((block) => (
                <Card key={block.id}>
                  <CardContent className="pt-6">
                    <div className="flex flex-wrap justify-between items-start gap-3">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{block.timeRange}</span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {block.type}: {block.callCount} calls
                        </div>
                      </div>
                      <Button variant="outline" size="sm">
                        View Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* ASAP List Management */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Phone className="h-5 w-5 text-muted-foreground" />
              ASAP List Management
            </h2>
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="space-y-2">
                  <div className="font-medium">Today's Openings:</div>
                  <div className="flex flex-wrap gap-2">
                    {openSlots.map((slot, index) => (
                      <Badge key={index} variant="secondary">
                        {slot.time} ({slot.duration})
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="font-medium">Priority Patients:</div>
                  <div className="text-sm text-muted-foreground">
                    5 patients waiting for appointments
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Start ASAP Calls
                  </Button>
                  <Button variant="outline" className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Update Availability
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SchedulingPage;
